/**
 * What a month's money comes to (migrations 0011, 0070).
 *
 * Two different figures were both called "Cobrado", which is what made the
 * card on Cobros wrong: the money that came in during a month, and the part of
 * it that belongs to that month's sessions. A patient who pays for four
 * sessions up front hands over the money in March; the sessions may be March's,
 * April's, or not booked yet. Both figures are true, and the screen shows both.
 *
 * A payment covers a number of sessions, not an amount per session, so the part
 * of it that has not covered one yet is that same share of the money.
 */

const number = (value) => Number(value ?? 0) || 0;

const total = (rows, of) => (rows ?? []).reduce((sum, row) => sum + of(row), 0);

/**
 * The part of a payment that has not reached a session yet.
 *
 * Since 0072 a payment is money put against debt, and patient_payment_balances
 * says exactly how much of it is left (remaining_amount). The share in
 * proportion to sessions is what a row from before that reads as.
 */
export const unassignedAmount = (payment) => {
  if (payment?.remaining_amount !== undefined && payment?.remaining_amount !== null) {
    return Math.max(0, number(payment.remaining_amount));
  }
  const covered = number(payment?.sessions_covered);
  const remaining = Math.min(number(payment?.sessions_remaining), covered);
  if (covered <= 0 || remaining <= 0) return 0;
  return (number(payment?.amount) * remaining) / covered;
};

/**
 * The money that arrived: what patients paid, what obras sociales paid, and
 * how much of the patients' part is not against a session yet.
 *
 * `payments` are rows of patient_payment_balances; `planPaid` are sessions
 * whose obra social has paid, which is the only record there is of that money.
 */
export const collected = (payments, planPaid) => {
  const fromPatients = total(payments, (row) => number(row.amount));
  const fromPlans = total(planPaid, (row) => number(row.plan_amount));
  return {
    fromPatients,
    fromPlans,
    total: fromPatients + fromPlans,
    unassigned: total(payments, unassignedAmount),
  };
};

/**
 * Of the sessions given, what has been collected against them: the patient's
 * price where the patient has paid, the obra social's where it has. This is
 * the figure that answers "how did this month's sessions do", as opposed to
 * how much money came in during the month.
 */
export const collectedForSessions = (sessions) => (sessions ?? []).reduce((sum, row) => {
  // What reached the session (0072), which a part-paid one has too; a row
  // without it counts its price when paid, as before.
  const patient = row.patient_paid_amount !== undefined && row.patient_paid_amount !== null
    ? number(row.patient_paid_amount)
    : (row.patient_payment_status === 'paid' ? number(row.patient_amount) : 0);
  const plan = row.plan_payment_status === 'paid' ? number(row.plan_amount) : 0;
  return sum + patient + plan;
}, 0);
